import { client } from '@/sdk/client.gen';
import { isUnexpectedApiFailure } from '@/common/apiRequestError';
import { captureException } from '@/posthog-analytics/posthog';
import { ApiError, NETWORK_ERROR_TYPE, rethrow } from './errors';

function getCsrfToken(): string {
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrftoken='))
      ?.split('=')[1] ?? ''
  );
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

interface SdkConfig {
  onUnauthenticated?: () => void;
}

// Same shape the old fetchWithCsrf sent, so existing PostHog filters on tags.api_url keep
// matching. Request headers are left out: they carry the CSRF token.
function recordApiFailure(
  error: unknown,
  request: Request | undefined,
  status: number,
  responseData: unknown,
  shouldReport: boolean,
): void {
  const url = request ? new URL(request.url) : undefined;
  const apiUrl = url ? url.pathname + url.search : '';
  if (shouldReport) {
    captureException(error, {
      extra: {
        requestConfig: { method: request?.method, url: apiUrl },
        responseStatus: status,
        responseData,
      },
      tags: { api_url: apiUrl, api_status_code: status },
    });
  }
  console.error(`Server responded with an error: ${apiUrl}:`, { status, data: responseData });
}

export function initSdk(config: SdkConfig = {}): void {
  client.setConfig({ baseUrl: '/asyncapi/v2' });

  client.interceptors.request.use((request) => {
    if (!SAFE_METHODS.has(request.method.toUpperCase())) {
      request.headers.set('X-CSRFToken', getCsrfToken());
    }
    return request;
  });

  client.interceptors.error.use((body, response, request) => {
    if (!response) {
      const networkError = new ApiError(
        'Network error: the request did not complete',
        0,
        NETWORK_ERROR_TYPE,
        { cause: body },
      );
      recordApiFailure(networkError, request, 0, undefined, !request?.signal.aborted);
      throw networkError;
    }
    if (response.status === 401) {
      config.onUnauthenticated?.();
    }
    try {
      rethrow(body, response.status);
    } catch (error) {
      recordApiFailure(error, request, response.status, body, isUnexpectedApiFailure(response.status, body));
      throw error;
    }
  });
}

export { client };
